import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { BarChart3, Download, TrendingUp } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const categoryData = [
  { name: 'NF EL', value: 105, color: '#1A3C6E' },
  { name: 'Fiction EL', value: 180, color: '#2E5F8A' },
  { name: 'MTL-CL', value: 81, color: '#DC2626' },
  { name: 'MTL-ML', value: 32, color: '#16A34A' },
  { name: 'MTL-TL', value: 13, color: '#F59E0B' },
];

const levelData = [
  { name: 'Primary', value: 140, color: '#10B981' },
  { name: 'Secondary', value: 138, color: '#3B82F6' },
  { name: 'Pre-U', value: 133, color: '#8B5CF6' },
];

const selectionData = [
  { name: 'Submitted', value: 248, color: '#10B981' },
  { name: 'Draft', value: 12, color: '#F59E0B' },
  { name: 'Not Started', value: 27, color: '#EF4444' },
];

const topFictionTitles = [
  { title: 'Between Two Worlds', selections: 247 },
  { title: 'The Memory Keeper', selections: 231 },
  { title: 'Code Red', selections: 218 },
  { title: 'The Last Library', selections: 205 },
  { title: 'Daughters of Singapore', selections: 198 },
  { title: 'Sound of Silence', selections: 187 },
  { title: 'Island Voices', selections: 176 },
];

const budgetData = [
  { school: 'RGS', budget: 3000, committed: 2847.50, level: 'Sec' },
  { school: 'ACS (I)', budget: 3000, committed: 2980.00, level: 'Sec' },
  { school: 'Nanyang Pri', budget: 3000, committed: 2650.00, level: 'Pri' },
  { school: 'VJC', budget: 3000, committed: 2870.00, level: 'Pre-U' },
  { school: 'Queenstown', budget: 3000, committed: 2750.00, level: 'Sec' },
];

export default function MOEReports() {
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-[#1A3C6E]" />
            Reports & Analytics
          </h1>
          <p className="text-slate-500 mt-1">Data as at: Nov 2026</p>
        </div>
        <Button variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Export All Reports
        </Button>
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="dashboard">Programme Dashboard</TabsTrigger>
          <TabsTrigger value="budget">Budget Utilisation</TabsTrigger>
          <TabsTrigger value="kpis">Delivery KPIs</TabsTrigger>
          <TabsTrigger value="school">School-Level Reports</TabsTrigger>
          <TabsTrigger value="annual">Annual Reports</TabsTrigger>
        </TabsList>

        {/* Programme Dashboard */}
        <TabsContent value="dashboard" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Titles by Category */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-base">CDS Cycle 1 2028 Titles by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Titles by Level */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-base">By Level</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={levelData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {levelData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Selection Status */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-base">School Selection Status</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={selectionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {selectionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Top Fiction Titles */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="font-heading">Top 10 Optional Fiction Titles Selected by Schools</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topFictionTitles} layout="vertical" margin={{ left: 120 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="title" type="category" width={120} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="selections" fill="#1A3C6E" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Budget Utilisation */}
        <TabsContent value="budget" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <Card className="bg-slate-50">
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-slate-900">$861,000</div>
                <div className="text-sm text-slate-500">Total Budget</div>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50">
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-emerald-700">$802,150</div>
                <div className="text-sm text-emerald-600">Committed (93.2%)</div>
              </CardContent>
            </Card>
            <Card className="bg-blue-50">
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-blue-700">$58,850</div>
                <div className="text-sm text-blue-600">Remaining</div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading">School Budget Utilisation</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>School</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Budget</TableHead>
                    <TableHead>Committed</TableHead>
                    <TableHead>Remaining</TableHead>
                    <TableHead>% Used</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {budgetData.map((school, idx) => {
                    const pct = ((school.committed / school.budget) * 100).toFixed(1);
                    const pctClass = parseFloat(pct) > 95 ? 'text-red-600' : parseFloat(pct) > 85 ? 'text-amber-600' : 'text-emerald-600';
                    return (
                      <TableRow key={idx} className="table-row-striped">
                        <TableCell className="font-medium">{school.school}</TableCell>
                        <TableCell>{school.level}</TableCell>
                        <TableCell>${school.budget.toLocaleString()}</TableCell>
                        <TableCell>${school.committed.toLocaleString()}</TableCell>
                        <TableCell>${(school.budget - school.committed).toFixed(2)}</TableCell>
                        <TableCell className={pctClass}>{pct}%</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Delivery KPIs */}
        <TabsContent value="kpis" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">4.8 months</div>
                    <div className="text-sm text-slate-500">Avg time from selection to first batch</div>
                    <div className="text-xs text-emerald-600">✓ Target ≤5 months</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">99.6%</div>
                    <div className="text-sm text-slate-500">Books in good condition on arrival</div>
                    <div className="text-xs text-emerald-600">✓ Excellent</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">98.2%</div>
                    <div className="text-sm text-slate-500">Supplier quote compliance (≥3 quotes)</div>
                    <div className="text-xs text-emerald-600">✓ Compliant</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">94.4%</div>
                    <div className="text-sm text-slate-500">School acknowledgement within 5 days</div>
                    <div className="text-xs text-emerald-600">✓ Good</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Annual Reports */}
        <TabsContent value="annual" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Available Reports</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <div className="font-medium">CDS Pilot Summary Report</div>
                    <div className="text-sm text-slate-500">Mar–Oct 2026</div>
                  </div>
                  <Button variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
                <div className="flex items-center justify-between p-4 border rounded-lg opacity-50">
                  <div>
                    <div className="font-medium">School Readership Report 2028</div>
                    <div className="text-sm text-slate-500">Available Nov 2028</div>
                  </div>
                  <Button variant="outline" disabled>
                    Set Reminder
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
